import type { ReactNode } from 'react';
import type { CalloutGlyphName } from './icons.js';
import { CALLOUT_GLYPHS } from './icons.js';
import './Callout.css';

export type CalloutTone = 'info' | 'warning' | 'danger' | 'success' | 'neutral' | 'pending';

export interface CalloutProps {
  /**
   * The tone. `pending` is the dashed "not ready yet" form the prototype uses on
   * six screens for data that has not arrived — deliberately a tone rather than a
   * separate component, because it occupies the same slot and swapping between
   * them must not change the layout.
   */
  tone?: CalloutTone;
  /** Short, sentence-case. Optional: some callouts are a single body sentence. */
  title?: ReactNode;
  children: ReactNode;
  /**
   * A leading glyph, named from the sanctioned `CALLOUT_GLYPHS` set — typeset,
   * not drawn, the same distinction the prototype makes in menus. A *name* rather
   * than a string so a call site cannot paste a raw dingbat, which is what
   * `lint:tokens`'s glyph-icon rule exists to prevent. Always decorative: the tone
   * is carried by the copy, never by the glyph alone.
   */
  glyph?: CalloutGlyphName;
  /** Buttons or links. Wraps on narrow viewports rather than overflowing. */
  actions?: ReactNode;
  /**
   * Raises the border to 1.5px. Reserved for the three places the prototype uses
   * it — a mass-deletion safeguard, a purge, a non-reversible confirmation — so it
   * keeps meaning something. Not a decoration for "important".
   */
  emphatic?: boolean;
  /**
   * Announce immediately via `role="alert"`.
   *
   * Off by default even for `danger`, and that is deliberate: most danger callouts
   * are present when the screen loads, and an alert that fires on page load
   * interrupts a screen-reader user before they have any context for it. Turn it
   * on when the callout appears *in response* to something the user just did.
   */
  live?: boolean;
}

/**
 * Inline alert / explanation block (design-system catalogue, Tier 2).
 *
 * **The most-repeated non-trivial pattern in the design — 17 of the prototype's
 * 18 page files** (prototype/CENSUS.md §2a). It went unbuilt for three phases
 * because it looks like `Card`, and every screen open-coded it instead.
 *
 * It is not `Card`, and the difference is structural rather than cosmetic:
 *
 *   Card     16px radius · 20px padding · a 15px heading · a grouping surface
 *            with `overflow: hidden` that *contains* other components
 *   Callout  12px radius · 13/15px padding · a 13.5px title · a statement about
 *            the screen, whose BODY TEXT takes the tone colour
 *
 * That last point is the one most easily lost: in a tinted callout the body is
 * the tone's own strong foreground, not `--text-secondary`. Using the neutral
 * secondary is what made early drafts of these read washed-out and detached from
 * their own border.
 *
 * Accessibility: the tone is always paired with the copy (a11y.md §2) — a reader
 * who cannot distinguish the tint gets the same information from the text. The
 * glyph is `aria-hidden`, since "warning triangle" announced before the sentence
 * adds nothing the sentence does not say.
 */
export function Callout({
  tone = 'neutral',
  title,
  children,
  glyph,
  actions,
  emphatic,
  live,
}: CalloutProps) {
  return (
    <div
      className={[
        'dpr-callout',
        `dpr-callout--${tone}`,
        emphatic ? 'dpr-callout--emphatic' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      {...(live ? { role: 'alert' } : {})}
    >
      {title ? (
        <p className="dpr-callout__head">
          {glyph ? (
            <span className="dpr-callout__glyph" aria-hidden="true">
              {CALLOUT_GLYPHS[glyph]}
            </span>
          ) : null}
          <span className="dpr-callout__title">{title}</span>
        </p>
      ) : null}
      <div className="dpr-callout__body">{children}</div>
      {actions ? <div className="dpr-callout__actions">{actions}</div> : null}
    </div>
  );
}
